/**
 * #609 — ¿el precio unitario de esta línea lo dijo un humano, o lo puso el modelo?
 *
 * ===========================================================================
 * LANE PARITY — este archivo tiene un GEMELO BYTE-EQUIVALENTE en
 * `mcp-server/src/utils/priceProvenance.js`.
 * ===========================================================================
 * Mismo recorte que `unitProvenance.js` (#604), `auditRef.js`, `materialSplit.js`
 * y `rawTextFidelity.js`: `src/**` es el bundle del browser y `mcp-server/**` es
 * un paquete Node aparte, así que no se importan entre sí. El test de paridad
 * `mcp-server/tests/tools/priceProvenanceLaneParity.test.js` compara los cuerpos
 * byte a byte y corre la MISMA tabla de casos. Si tocás una copia, tocá la otra.
 *
 * ---------------------------------------------------------------------------
 * LO MEDIDO (#609)
 * ---------------------------------------------------------------------------
 * `tests/mcp-agent-suite/results/admin-2026-08-09T03-27-20-923e.json`, turno 1.
 * El usuario dictó, textual:
 *
 *     "Necesito una cotizacion para el proyecto Q148EQ-SL-0516: 2 Mini Split
 *      24000 BTU y 8 horas de instalacion."
 *
 * Ahí no hay un solo precio. Lo que el agente mandó:
 *
 *     add_quotation_item(... "Mini Split 24000 BTU", unit_price: 350000)
 *     add_quotation_item(... "Instalación de Minisplit", unit_price:  25000)
 *
 * Y `unit_price` **SÍ es `required`** en `add_quotation_item`. O sea: `required`
 * garantiza que el campo llegue lleno, no que lo haya llenado el humano. Un
 * campo obligatorio que el modelo puede completar de memoria es obligatorio
 * para el *schema* y opcional para la *verdad*.
 *
 * Duele más que la unidad de #604 por dos motivos: una cotización se le manda a
 * un cliente, y ₡350.000 por un Mini Split es **plausible** — no le llama la
 * atención a nadie que la lea por encima. Se firma.
 *
 * ---------------------------------------------------------------------------
 * QUÉ PUEDE PROBAR ESTE MÓDULO, Y QUÉ NO
 * ---------------------------------------------------------------------------
 * Compara DOS COSAS QUE EL SERVER TIENE: el número que vino en `unit_price` y
 * el texto del usuario. Es el mismo mecanismo server-knowable de #369
 * (`raw_text`) y #604 (`unit`), aplicado al campo que decide cuánto se le cobra
 * al cliente. No le pregunta nada al modelo y no depende de ningún auto-reporte.
 *
 * Los límites, de frente:
 *
 *  - **No prueba que el número fuera dicho COMO precio.** "2 Mini Split 24000
 *    BTU": si el modelo cotiza a ₡24.000 la unidad, esto lo da por dictado. El
 *    server puede probar que el número se dijo, no qué modificaba. Es el mismo
 *    recorte que `unitProvenance` documenta para "codos de una pulgada".
 *  - **No deriva el unitario de un total.** Si el usuario dice "los dos me
 *    salen 700 mil" y el modelo pone 350000 la unidad, esto lo marca inventado
 *    y el agente termina preguntando. Es el lado correcto para equivocarse.
 *  - **El texto lo manda el llamador.** Un modelo que quisiera burlarlo tendría
 *    que FORJAR las palabras del usuario, que es un paso mucho más grande que
 *    rellenar un número obligatorio — pero es posible, y por eso esto no es una
 *    prueba criptográfica sino una compuerta. Mismo recorte que documenta
 *    `rawTextFidelity.js`.
 *  - **Sin texto no hay contra qué comparar** (`unverifiable`). Eso NO es un
 *    aprobado tácito: es que el server no puede afirmar nada, y por eso el
 *    caller lo trata igual que a un precio inventado. Si `unverifiable` pasara,
 *    el guard se apagaría solo con omitir un campo.
 */

/** Multiplicadores que se dicen en voz alta en Costa Rica. */
const MULTIPLIERS = {
  mil: 1000,
  miles: 1000,
  k: 1000,
  millon: 1000000,
  millones: 1000000,
  palo: 1000000,
  palos: 1000000,
};

/**
 * Normaliza para comparar: minúsculas, sin acentos, símbolos de moneda fuera,
 * espacios colapsados. Se dejan los dígitos, el punto y la coma porque son
 * separadores de miles/decimales y hacen falta para interpretar el número.
 */
export const normalizePriceText = (text) => {
  if (text == null) return '';
  return String(text)
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^a-z0-9.,]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

/** El precio tal como lo mandó el llamador, o null si no mandó un número. */
export const parseSuppliedPrice = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : null;
};

/**
 * Interpreta UN token numérico. Convención tica: el punto agrupa miles
 * ("₡350.000" = 350000) y la coma separa decimales ("350,50" = 350.5). Un token
 * con 1 o 2 dígitos después del separador se lee como decimal; cualquier otra
 * cosa se lee como agrupación de miles.
 */
const readNumericToken = (token) => {
  if (/^\d+[.,]\d{1,2}$/.test(token)) {
    return Number(token.replace(',', '.'));
  }
  const digits = token.replace(/[.,]/g, '');
  if (digits === '') return null;
  const n = Number(digits);
  return Number.isFinite(n) ? n : null;
};

/**
 * Todos los montos que aparecen en el texto del usuario.
 *
 * Cuando al número lo sigue un multiplicador hablado ("350 mil", "1.5
 * millones") se devuelve SOLO el valor multiplicado, no el desnudo: "350 mil"
 * no autoriza un precio de 350. Ser permisivo acá haría pasar por dictado un
 * número que nadie dijo, que es justamente lo que este módulo existe para
 * impedir.
 *
 * @param {unknown} text
 * @returns {number[]} montos, en el orden en que aparecen
 */
export const extractStatedAmounts = (text) => {
  const src = normalizePriceText(text);
  if (src === '') return [];

  const out = [];
  const re = /(\d[\d.,]*?)(?=[^\d.,]|$)(?:\s*(mil|miles|k|millones|millon|palos|palo)(?![a-z0-9]))?/g;
  let m;
  while ((m = re.exec(src)) !== null) {
    const base = readNumericToken(m[1]);
    if (base === null) continue;
    const mult = m[2] ? MULTIPLIERS[m[2]] : null;
    out.push(mult ? base * mult : base);
  }
  return out;
};

/** Frases con las que un humano dice "esta línea no se cobra". */
const FREE_OF_CHARGE = /\b(gratis|sin costo|sin cargo|cortesia|regalado|incluido|incluida)\b/;

/** Procedencia posible del precio unitario de una línea. */
export const PRICE_SOURCE = {
  /** No vino precio. */
  ABSENT: 'absent',
  /** El número aparece en el texto del usuario: lo dijo un humano. */
  DICTATED: 'dictated',
  /** Hay texto del usuario y el número NO está ahí: lo puso el modelo. */
  INVENTED: 'invented',
  /** No vino el texto del usuario: no hay contra qué comparar. */
  UNVERIFIABLE: 'unverifiable',
};

/**
 * Lo que se GUARDA en `quotation_items.price_source`. Es distinto de
 * `PRICE_SOURCE` a propósito: aquello clasifica la llamada, esto describe la
 * fila. NULL en la columna = fila anterior a #609 (no se re-juzga).
 */
export const PRICE_ROW_SOURCE = {
  /** Lo dijo un humano — dictado al agente o tipeado en el formulario web. */
  STATED: 'stated',
  /** Lo puso el servidor desde `inventory.sale_price`. */
  CATALOG: 'catalog',
  /** Nadie confirmó este número. La cotización no sale así. */
  UNCONFIRMED: 'unconfirmed',
};

/** Tolerancia de comparación: dos décimas de céntimo. */
const EPSILON = 0.005;

/**
 * @param {unknown} suppliedPrice   unit_price tal como vino en la llamada
 * @param {unknown} sourceInputText las palabras del usuario para esta línea
 * @returns {string} uno de PRICE_SOURCE
 */
export const classifyPriceProvenance = (suppliedPrice, sourceInputText) => {
  const price = parseSuppliedPrice(suppliedPrice);
  if (price === null) return PRICE_SOURCE.ABSENT;

  const src = typeof sourceInputText === 'string' ? sourceInputText.trim() : '';
  if (src === '') return PRICE_SOURCE.UNVERIFIABLE;

  // "Se lo dejamos gratis" es un 0 que un humano decidió, igual que el 0% de
  // margen de #593: la decisión existe aunque el dígito no esté escrito.
  if (price === 0 && FREE_OF_CHARGE.test(normalizePriceText(src))) {
    return PRICE_SOURCE.DICTATED;
  }

  const said = extractStatedAmounts(src);
  const match = said.some((n) => Math.abs(n - price) < EPSILON);
  return match ? PRICE_SOURCE.DICTATED : PRICE_SOURCE.INVENTED;
};

/**
 * ¿Cómo se guarda esta línea? Resuelve las tres ramas de #609 en un solo lugar
 * para que los dos carriles no puedan discrepar en el orden.
 *
 * EL ORDEN IMPORTA, y es el mismo cuidado que #604: **el catálogo nunca pisa un
 * precio dictado.** Si el usuario dijo "el Mini Split sale 450000", ese es el
 * precio aunque el catálogo diga otra cosa — pisarlo sería cobrarle al cliente
 * un número que el vendedor no acordó. El catálogo sólo entra donde el modelo
 * inventó o donde no se puede verificar.
 *
 * @param {object} args
 * @param {unknown} args.suppliedPrice   unit_price de la llamada
 * @param {unknown} args.sourceInputText las palabras del usuario
 * @param {unknown} args.catalogPrice    inventory.sale_price, si hay link
 * @returns {{source: string, provenance: string, unitPrice: number|null, catalogPrice: number|null}}
 */
export const resolveItemPrice = ({ suppliedPrice, sourceInputText, catalogPrice }) => {
  const provenance = classifyPriceProvenance(suppliedPrice, sourceInputText);
  const supplied = parseSuppliedPrice(suppliedPrice);

  if (provenance === PRICE_SOURCE.DICTATED) {
    return { source: PRICE_ROW_SOURCE.STATED, provenance, unitPrice: supplied, catalogPrice: null };
  }

  const fromCatalog = parseSuppliedPrice(catalogPrice);
  if (fromCatalog !== null && fromCatalog > 0) {
    return { source: PRICE_ROW_SOURCE.CATALOG, provenance, unitPrice: fromCatalog, catalogPrice: fromCatalog };
  }

  return { source: PRICE_ROW_SOURCE.UNCONFIRMED, provenance, unitPrice: supplied, catalogPrice: null };
};

/**
 * El aviso que el server le devuelve al agente cuando le guardó un precio que
 * nadie confirmó. Nombra el número rechazado a propósito: sin eso el modelo
 * vuelve a mandar el mismo y no entiende por qué la línea sigue trabada (misma
 * lección que #604 con la unidad).
 */
export const unconfirmedPriceWarning = (items) => {
  const detalle = items
    .map((i) => `"${i.description}" (mandaste ${i.claimed_price})`)
    .join(', ');
  return (
    `${items.length} línea(s) quedaron con un precio que NADIE confirmó: ${detalle}. ` +
    'Ese número no aparece en lo que dijo el usuario, así que el servidor lo marcó ' +
    'como sin confirmar y la cotización NO se va a poder aprobar ni marcar como ' +
    'enviada hasta que se resuelva. NO lo repitas ni lo defiendas: preguntale al ' +
    'usuario cuánto vale cada uno y corregí con update_quotation_item mandando ' +
    'también `source_input_text` con lo que él te contestó, textual (#609).'
  );
};

/** El aviso cuando el precio lo puso el servidor desde el catálogo. */
export const catalogPriceNotice = (items) => {
  const detalle = items
    .map((i) => `"${i.description}" (mandaste ${i.claimed_price}, el catálogo dice ${i.catalog_price})`)
    .join(', ');
  return (
    `${items.length} línea(s) traían un precio que NO aparece en lo que dijo el usuario: ` +
    `${detalle}. El servidor lo reemplazó por el precio de lista del inventario ` +
    '(`sale_price`) y lo marcó como tal. Decíselo así al usuario: el precio salió del ' +
    'catálogo, no de él, y puede cambiarlo si quiere (#609).'
  );
};

/**
 * El mensaje de la compuerta al aprobar / enviar. Gemelo de
 * `assertMarginDecided` (#593): misma compuerta, otro campo.
 */
export const unconfirmedPricesMessage = (items, action) => {
  const detalle = items
    // #617: sin `₡`. `quotation_items` no guarda moneda; el simbolo lo ponia
    // el mensaje, no el dato. El numero solo ya identifica la linea.
    .map((i) => `"${i.description}" (${i.unit_price})`)
    .join(', ');
  return (
    `No se puede ${action} esta cotización: ${items.length} línea(s) llevan un precio ` +
    `que nadie confirmó — ${detalle}. Esos números no salieron del usuario ni del ` +
    'catálogo de inventario: los puso el asistente. Un precio inventado es plausible ' +
    'y se firma sin que nadie lo note, así que hay que confirmarlo antes de que la ' +
    'cotización salga hacia el cliente. Preguntale el monto al usuario y corregí cada ' +
    'línea con update_quotation_item (#609).'
  );
};

const priceProvenance = {
  PRICE_SOURCE,
  PRICE_ROW_SOURCE,
  normalizePriceText,
  parseSuppliedPrice,
  extractStatedAmounts,
  classifyPriceProvenance,
  resolveItemPrice,
  unconfirmedPriceWarning,
  catalogPriceNotice,
  unconfirmedPricesMessage,
};

export default priceProvenance;
